import { useState } from 'react'

import {
  Box,
  Form,
  Icon,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import DurationInput from 'modules/core/components/DurationInput'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  WizardData,
  WizardDataField,
  validateMandatoryFields,
} from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'

const HourlyRateStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const mandatoryFields: WizardDataField[] = ['rawMinutes', 'equivalentMinutes']

  const handleNext = async () => {
    setIsSubmitted(true)

    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    track(trackingEvents.timeTracking.newSpecialHourStep2NextPressed, {
      errors: invalidFields.length ? JSON.stringify(invalidFields) : undefined,
      rawMinutes: data.rawMinutes,
      equivalentMinutes: data.equivalentMinutes,
      isEdit: data.isEdit,
      id: data.id,
    })

    if (invalidFields.length) {
      return Promise.reject()
    }
  }

  useOnNext(handleNext)

  const handleDataChange = (newData: Partial<WizardData>) => {
    const updatedData = { ...data, ...newData }

    if (isSubmitted) {
      const { dataWithErrors } = validateMandatoryFields(mandatoryFields, updatedData)
      onDataChanged(dataWithErrors)
    } else {
      onDataChanged(updatedData)
    }
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s300">
        <Box gap="s8">
          <Text.Header2 weight="semibold">
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.hourly_rate.title'
            )}
          </Text.Header2>
          <Text.Normal>
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.hourly_rate.description'
            )}
          </Text.Normal>
        </Box>

        <Box flexDirection="row" gap="s16">
          <Form padding="s0" notRounded>
            <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceEvenly" gap="s8">
              <Box width="full">
                <DurationInput
                  label={i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.hourly_rate.worked_time'
                  )}
                  minutes={data.rawMinutes}
                  onChange={(value: number) => handleDataChange({ ...data, rawMinutes: value })}
                />
              </Box>
              <Box paddingTop="s32" width="fit-content">
                <Icon.Medium icon="arrow-right" />
              </Box>
              <Box width="full">
                <DurationInput
                  label={i18n.t(
                    'time_tracking.settings.time_blocks.categories.special_hours.wizard.hourly_rate.equals'
                  )}
                  minutes={data.equivalentMinutes}
                  onChange={(value: number) =>
                    handleDataChange({ ...data, equivalentMinutes: value })
                  }
                  error={data.errors?.equivalentMinutes}
                />
              </Box>
            </Box>
          </Form>
        </Box>
      </Box>
    </Box>
  )
}

export const hourlyRateStep = (): WizardStep<WizardData> => ({
  title: i18n.t(
    'time_tracking.settings.time_blocks.categories.special_hours.wizard.hourly_rate.title'
  ),
  nextText: i18n.t('time_tracking.settings.time_blocks.categories.special_hours.wizard.next'),
  render: (props) => <HourlyRateStep {...props} />,
  isCompleted: (data) => !!data.equivalentMinutes && !!data.rawMinutes,
})
